import React, { useRef, useState, useEffect } from "react";
import "../styles/StudentProfile.css";
import API_URL from "../apiConfig";

function StudentProfile({ user, token }) {
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();

  useEffect(() => {
    const fetchProfile = async () => {
      setError("");
      try {
        const studentId = user.student_id || user.id;
        const res = await fetch(`${API_URL}/api/students/${studentId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error("Profile fetch failed");
        const data = await res.json();
        setProfile(data);
      } catch {
        setError("Could not fetch profile.");
      }
    };
    fetchProfile();
  }, [user, token]);

  // Optional: You can remove these photo functions if not needed for students
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file.");
      return;
    }
    setError("");
    setPreview(URL.createObjectURL(file));
  };

  const handleUpload = async () => {
    const file = fileInputRef.current.files[0];
    if (!file) return;
    setUploading(true);
    setError("");
    const studentId = user.student_id || user.id;
    const formData = new FormData();
    formData.append("photo", file);
    try {
      const res = await fetch(`${API_URL}/api/students/${studentId}/photo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (!res.ok) throw new Error("Upload failed");
      setPreview(null);
      fileInputRef.current.value = "";
      const refreshed = await fetch(`${API_URL}/api/students/${studentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(await refreshed.json());
    } catch {
      setError("Upload failed. Try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete your profile photo?")) return;
    setUploading(true);
    setError("");
    const studentId = user.student_id || user.id;
    try {
      const res = await fetch(`${API_URL}/api/students/${studentId}/photo`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Delete failed");
      setPreview(null);
      fileInputRef.current.value = "";
      const refreshed = await fetch(`${API_URL}/api/students/${studentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(await refreshed.json());
    } catch {
      setError("Delete failed. Try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="student-profile">
      <h2 className="profile-heading">Student Profile</h2>
      <div className="student-profile-avatar-box">
        <img
          src={preview || profile?.profile_photo_url || "/default-avatar.png"}
          alt="Profile"
          className="student-profile-avatar"
        />
      </div>
      <div className="student-profile-actions">
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: "none" }}
          disabled={uploading}
        />
        <button
          className="student-profile-btn"
          onClick={() => fileInputRef.current.click()}
          disabled={uploading}
        >
          {profile?.profile_photo_url ? "Change Photo" : "Upload Photo"}
        </button>
        {profile?.profile_photo_url && (
          <button
            className="student-profile-btn delete"
            onClick={handleDelete}
            disabled={uploading}
          >
            Delete Photo
          </button>
        )}
      </div>
      {preview && (
        <div className="student-profile-actions">
          <button
            className="student-profile-btn save"
            onClick={handleUpload}
            disabled={uploading}
          >
            {uploading ? "Uploading..." : "Save Photo"}
          </button>
          <button
            className="student-profile-btn cancel"
            onClick={() => { setPreview(null); fileInputRef.current.value = ""; }}
            disabled={uploading}
          >
            Cancel
          </button>
        </div>
      )}
      {error && <div className="student-profile-error">{error}</div>}
      <div className="student-profile-info">
        <div className="student-profile-info-row">
          <span>Name:</span>
          <span>{profile?.name || "-"}</span>
        </div>
        <div className="student-profile-info-row">
          <span>Course:</span>
          <span>{profile?.course || "—"}</span>
        </div>
        <div className="student-profile-info-row">
          <span>Email:</span>
          <span>{profile?.email || "-"}</span>
        </div>
        <div className="student-profile-info-row">
          <span>Roll No:</span>
          <span>{profile?.roll_no || "-"}</span>
        </div>
        {/* Add more fields as needed */}
      </div>
    </div>
  );
}

export default StudentProfile;